// Alerts autosave state (rcm-06 §4). Mock only: the Alerts page flips this to
// 'saving' on every change, then to 'saved' (or 'error') after a short delay,
// and the shared settings band reads it. No backend; each save is written to a
// mock in-memory audit log.
import { useSyncExternalStore } from 'react';

export type AlertsSaveState = 'idle' | 'saving' | 'saved' | 'error';

interface AuditEntry {
  at: string;
  who: string;
  what: string;
}

let state: AlertsSaveState = 'idle';
const audit: AuditEntry[] = [];
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setTimeout> | null = null;

function emit(): void {
  for (const l of listeners) l();
}

export function subscribeAlertsSave(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}
export function getAlertsSaveState(): AlertsSaveState {
  return state;
}
export function useAlertsSaveState(): AlertsSaveState {
  return useSyncExternalStore(subscribeAlertsSave, getAlertsSaveState, getAlertsSaveState);
}

/** The mock audit trail (every autosave writes one entry). */
export function getAlertsAudit(): readonly AuditEntry[] {
  return audit;
}

/**
 * Record a change and run the mock autosave. `fail` (dev/testing only) forces
 * the error band state so the "Couldn't save. Try again" path can be seen.
 */
export function recordAlertsChange(what: string, who = 'Dr. Emily Carter', fail = false): void {
  if (timer) clearTimeout(timer);
  state = 'saving';
  emit();
  timer = setTimeout(() => {
    if (fail) {
      state = 'error';
    } else {
      state = 'saved';
      audit.unshift({ at: new Date().toISOString(), who, what });
    }
    emit();
    if (!fail) {
      timer = setTimeout(() => {
        state = 'idle';
        emit();
      }, 1600);
    }
  }, 700);
}
